/**
 * TranscriptionModal
 * 
 * Options modal shown before starting transcription. Allows users to:
 * - See file info (name, duration, size)
 * - Select language (Auto, Turkish, English)
 * - Toggle speaker diarization
 * - Toggle grammar correction
 * - Add optional analysis instructions
 * - Toggle ICF progress tracking
 */

import React, { useState } from 'react';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    TextInput,
    ScrollView,
    Switch,
    Platform,
    Keyboard,
    ActivityIndicator,
    StyleSheet,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { TranscriptionOptions, AudioFileInfo } from '../../types';
import { reviewDesk } from '../../design-system';
import { brand } from '../../design-system/theme/tokens';

// Design tokens matching ChatScreenEnhanced
const COLORS = {
    primary: brand.primary,
    secondary: '#F59E0B',
    success: '#10B981',
    error: '#EF4444',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: {
        primary: '#111827',
        secondary: '#4B5563',
        muted: '#9CA3AF',
    },
    border: '#E5E7EB',
};

interface TranscriptionModalProps {
    visible: boolean;
    onClose: () => void;
    onStart: (options: TranscriptionOptions) => void;
    file: AudioFileInfo | null;
    loading?: boolean;
}

// Format file size for display
function formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Format duration for display
function formatDuration(seconds?: number): string {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function TranscriptionModal({
    visible,
    onClose,
    onStart,
    file,
    loading = false,
}: TranscriptionModalProps) {
    const { t } = useTranslation();

    // Options state
    const [language, setLanguage] = useState<'auto' | 'tr' | 'en'>('auto');
    const [enableDiarization, setEnableDiarization] = useState(true);
    const [applyGrammarCorrection, setApplyGrammarCorrection] = useState(true);
    const [analysisInstructions, setAnalysisInstructions] = useState(
        t('transcription.modal.defaultICFInstructions')
    );
    const [includeInICFProgress, setIncludeInICFProgress] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const handleStart = () => {
        onStart({
            language: language === 'auto' ? undefined : language,
            enableDiarization,
            applyGrammarCorrection,
            analysisInstructions: analysisInstructions.trim() || undefined,
            includeInICFProgress,
        });
    };

    const resetAndClose = () => {
        setLanguage('auto');
        setEnableDiarization(true);
        setApplyGrammarCorrection(true);
        setAnalysisInstructions(
            t('transcription.modal.defaultICFInstructions')
        );
        setIncludeInICFProgress(true);
        setShowAdvanced(false);
        onClose();
    };

    if (Platform.OS === ('web' as any)) {
        return (
            <Modal
                visible={visible}
                animationType="fade"
                transparent
                onRequestClose={resetAndClose}
            >
                <View style={simpleStyles.backdrop}>
                    <View style={simpleStyles.sheet}>
                        <View style={simpleStyles.header}>
                            <TouchableOpacity onPress={resetAndClose} style={simpleStyles.closeButton}>
                                <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
                            </TouchableOpacity>
                            <View style={{ flex: 1 }}>
                                <Text style={simpleStyles.headerTitle}>
                                    {t('transcription.simple.title')}
                                </Text>
                                <Text style={simpleStyles.headerSubtitle}>
                                    {t('transcription.simple.subtitle')}
                                </Text>
                            </View>
                        </View>

                        {file && (
                            <View style={simpleStyles.fileCard}>
                                <View style={simpleStyles.fileIcon}>
                                    <MaterialIcons name="audiotrack" size={26} color={reviewDesk.accent} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={simpleStyles.fileName} numberOfLines={1}>{file.name}</Text>
                                    <Text style={simpleStyles.fileMeta}>
                                        {file.duration ? `${formatDuration(file.duration)} · ` : ''}
                                        {formatFileSize(file.size) || t('transcription.simple.audioFile')}
                                    </Text>
                                </View>
                                <View style={simpleStyles.readyPill}>
                                    <MaterialIcons name="check-circle" size={14} color={reviewDesk.accent} />
                                    <Text style={simpleStyles.readyPillText}>{t('transcription.simple.ready')}</Text>
                                </View>
                            </View>
                        )}

                        <View style={simpleStyles.defaultsCard}>
                            <Text style={simpleStyles.sectionTitle}>{t('transcription.simple.defaultsTitle')}</Text>
                            {[
                                ['language', t('transcription.simple.defaultLanguage')],
                                ['groups', t('transcription.simple.defaultSpeakers')],
                                ['edit-note', t('transcription.simple.defaultGrammar')],
                                ['verified-user', t('transcription.simple.defaultAnalysis')],
                            ].map(([icon, label]) => (
                                <View key={label} style={simpleStyles.defaultRow}>
                                    <MaterialIcons name={icon as any} size={18} color={reviewDesk.accent} />
                                    <Text style={simpleStyles.defaultText}>{label}</Text>
                                </View>
                            ))}
                        </View>

                        <View style={simpleStyles.pathCard}>
                            <Text style={simpleStyles.sectionTitle}>{t('transcription.simple.pathTitle')}</Text>
                            {[
                                t('transcription.simple.stepTranscribe'),
                                t('transcription.simple.stepSpeakers'),
                                t('transcription.simple.stepAnalyze'),
                                t('transcription.simple.stepChat'),
                            ].map((label, index) => (
                                <View key={label} style={simpleStyles.pathRow}>
                                    <View style={simpleStyles.stepCircle}>
                                        <Text style={simpleStyles.stepCircleText}>{index + 1}</Text>
                                    </View>
                                    <Text style={simpleStyles.defaultText}>{label}</Text>
                                </View>
                            ))}
                        </View>

                        {showAdvanced && (
                            <View style={simpleStyles.advancedBlock}>
                                <Text style={simpleStyles.sectionTitle}>{t('transcription.simple.advancedTitle')}</Text>
                                <View style={simpleStyles.segmentRow}>
                                    {[
                                        { value: 'auto', label: t('transcription.modal.autoDetect') },
                                        { value: 'tr', label: t('transcription.modal.turkish') },
                                        { value: 'en', label: t('transcription.modal.english') },
                                    ].map((option) => {
                                        const selected = language === option.value;
                                        return (
                                            <TouchableOpacity
                                                key={option.value}
                                                onPress={() => setLanguage(option.value as 'auto' | 'tr' | 'en')}
                                                style={[simpleStyles.segment, selected && simpleStyles.segmentActive]}
                                            >
                                                <Text style={[simpleStyles.segmentText, selected && simpleStyles.segmentTextActive]}>
                                                    {option.label}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                                {[
                                    {
                                        title: t('transcription.modal.speakerDiarization'),
                                        desc: t('transcription.modal.speakerDiarizationDesc'),
                                        value: enableDiarization,
                                        onValueChange: setEnableDiarization,
                                    },
                                    {
                                        title: t('transcription.modal.grammarCorrection'),
                                        desc: t('transcription.modal.grammarCorrectionDesc'),
                                        value: applyGrammarCorrection,
                                        onValueChange: setApplyGrammarCorrection,
                                    },
                                    {
                                        title: t('transcription.modal.icfProgressToggle'),
                                        desc: t('transcription.modal.icfProgressDesc'),
                                        value: includeInICFProgress,
                                        onValueChange: setIncludeInICFProgress,
                                    },
                                ].map((setting) => (
                                    <View key={setting.title} style={simpleStyles.settingRow}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={simpleStyles.settingTitle}>{setting.title}</Text>
                                            <Text style={simpleStyles.settingDesc}>{setting.desc}</Text>
                                        </View>
                                        <Switch
                                            value={setting.value}
                                            onValueChange={setting.onValueChange}
                                            trackColor={{ false: '#E3E7EE', true: 'rgba(30, 64, 175, 0.28)' }}
                                            thumbColor={setting.value ? reviewDesk.accent : '#9AA4B2'}
                                        />
                                    </View>
                                ))}
                                <TextInput
                                    style={simpleStyles.textArea}
                                    value={analysisInstructions}
                                    onChangeText={setAnalysisInstructions}
                                    placeholder={t('transcription.modal.analysisPlaceholder')}
                                    placeholderTextColor={reviewDesk.muted}
                                    multiline
                                    numberOfLines={4}
                                />
                            </View>
                        )}

                        <View style={simpleStyles.infoNote}>
                            <MaterialIcons name="info-outline" size={18} color={reviewDesk.amber} />
                            <Text style={simpleStyles.infoText}>
                                {t('transcription.simple.safeDefaults')}
                            </Text>
                        </View>

                        <View style={simpleStyles.footer}>
                            <TouchableOpacity onPress={() => setShowAdvanced((value) => !value)} style={simpleStyles.advancedLink}>
                                <Text style={simpleStyles.advancedLinkText}>
                                    {showAdvanced
                                        ? t('transcription.simple.hideSettings')
                                        : t('transcription.simple.changeSettings')}
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[simpleStyles.primaryAction, (loading || !file) && { opacity: 0.55 }]}
                                onPress={handleStart}
                                disabled={loading || !file}
                            >
                                {loading ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <>
                                        <Text style={simpleStyles.primaryActionText}>
                                            {t('transcription.simple.startCombined')}
                                        </Text>
                                        <MaterialIcons name="arrow-forward" size={20} color="#FFFFFF" />
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        );
    }

    return (
        <Modal
            visible={visible}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={resetAndClose}
        >
            <View style={[{ flex: 1 }, Platform.OS === ('web' as any) && ({ height: 'auto', maxHeight: '100vh' } as any)]}>
                <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.surface }}>
                    <ScrollView
                        style={{ flex: 1 }}
                        keyboardShouldPersistTaps="handled"
                        scrollEnabled={true}
                        keyboardDismissMode="on-drag"
                        onScrollBeginDrag={() => Keyboard.dismiss()}
                    >
                        {/* Header */}
                        <View style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: 'white',
                            paddingVertical: 12,
                            paddingHorizontal: 16,
                            borderBottomWidth: 1,
                            borderBottomColor: COLORS.border,
                        }}>
                            <TouchableOpacity onPress={resetAndClose}>
                                <MaterialIcons name="close" size={24} color={COLORS.text.secondary} />
                            </TouchableOpacity>
                            <Text style={{ fontSize: 18, fontWeight: '700', color: COLORS.text.primary }}>
                                {t('transcription.modal.title')}
                            </Text>
                            <Button
                                variant="default"
                                size="sm"
                                onPress={handleStart}
                                disabled={loading || !file}
                            >
                                <Text style={{ color: 'white', fontWeight: '600' }}>
                                    {t('transcription.modal.start')} →
                                </Text>
                            </Button>
                        </View>

                        {/* File Info */}
                        {file && (
                            <View style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                backgroundColor: 'white',
                                paddingHorizontal: 16,
                                paddingVertical: 12,
                                marginBottom: 1,
                            }}>
                                <MaterialIcons name="audiotrack" size={24} color={COLORS.primary} />
                                <View style={{ flex: 1, marginLeft: 12 }}>
                                    <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary }}>
                                        {file.name}
                                    </Text>
                                    <Text style={{ fontSize: 13, color: COLORS.text.muted, marginTop: 2 }}>
                                        {file.duration ? formatDuration(file.duration) : ''}
                                        {file.duration && file.size ? ' • ' : ''}
                                        {formatFileSize(file.size)}
                                    </Text>
                                </View>
                            </View>
                        )}

                        <View style={{ padding: 16 }}>
                            {/* Language Selection */}
                            <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 8 }}>
                                {t('transcription.modal.language')}
                            </Text>
                            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                                {[
                                    { value: 'auto', label: t('transcription.modal.autoDetect') },
                                    { value: 'tr', label: t('transcription.modal.turkish') },
                                    { value: 'en', label: t('transcription.modal.english') },
                                ].map((option) => (
                                    <TouchableOpacity
                                        key={option.value}
                                        onPress={() => setLanguage(option.value as 'auto' | 'tr' | 'en')}
                                        style={{
                                            flex: 1,
                                            paddingVertical: 10,
                                            paddingHorizontal: 12,
                                            borderRadius: 8,
                                            borderWidth: 2,
                                            borderColor: language === option.value ? COLORS.primary : COLORS.border,
                                            backgroundColor: language === option.value ? 'rgba(30, 64, 175, 0.08)' : 'white',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Text style={{
                                            fontSize: 14,
                                            fontWeight: language === option.value ? '600' : '500',
                                            color: language === option.value ? COLORS.primary : COLORS.text.secondary,
                                        }}>
                                            {option.label}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>

                            {/* Speaker Diarization Toggle */}
                            <Card style={{ marginBottom: 12, backgroundColor: 'white' }}>
                                <CardContent style={{ padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <View style={{ flex: 1, marginRight: 12 }}>
                                        <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 2 }}>
                                            {t('transcription.modal.speakerDiarization')}
                                        </Text>
                                        <Text style={{ fontSize: 13, color: COLORS.text.muted }}>
                                            {t('transcription.modal.speakerDiarizationDesc')}
                                        </Text>
                                    </View>
                                    <Switch
                                        value={enableDiarization}
                                        onValueChange={setEnableDiarization}
                                        trackColor={{ false: '#E5E7EB', true: '#93c5fd' }}
                                        thumbColor={enableDiarization ? COLORS.primary : '#9CA3AF'}
                                    />
                                </CardContent>
                            </Card>

                            {/* Grammar Correction Toggle */}
                            <Card style={{ marginBottom: 16, backgroundColor: 'white' }}>
                                <CardContent style={{ padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <View style={{ flex: 1, marginRight: 12 }}>
                                        <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 2 }}>
                                            {t('transcription.modal.grammarCorrection')}
                                        </Text>
                                        <Text style={{ fontSize: 13, color: COLORS.text.muted }}>
                                            {t('transcription.modal.grammarCorrectionDesc')}
                                        </Text>
                                    </View>
                                    <Switch
                                        value={applyGrammarCorrection}
                                        onValueChange={setApplyGrammarCorrection}
                                        trackColor={{ false: '#E5E7EB', true: '#93c5fd' }}
                                        thumbColor={applyGrammarCorrection ? COLORS.primary : '#9CA3AF'}
                                    />
                                </CardContent>
                            </Card>

                            {/* Post-Transcription Section */}
                            <View style={{
                                borderTopWidth: 1,
                                borderTopColor: COLORS.border,
                                paddingTop: 16,
                                marginTop: 8,
                            }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: COLORS.text.muted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {t('transcription.modal.postTranscription')}
                                </Text>

                                {/* Analysis Instructions */}
                                <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 8 }}>
                                    {t('transcription.modal.analysisInstructions')}
                                </Text>
                                <TextInput
                                    style={{
                                        backgroundColor: 'white',
                                        borderRadius: 8,
                                        padding: 12,
                                        fontSize: 14,
                                        minHeight: 80,
                                        borderWidth: 1,
                                        borderColor: COLORS.border,
                                        marginBottom: 16,
                                        textAlignVertical: 'top',
                                    }}
                                    value={analysisInstructions}
                                    onChangeText={setAnalysisInstructions}
                                    placeholder={t('transcription.modal.analysisPlaceholder')}
                                    placeholderTextColor={COLORS.text.muted}
                                    multiline
                                    numberOfLines={4}
                                />

                                {/* ICF Progress Toggle */}
                                <Card style={{ backgroundColor: '#F0F9FF', borderColor: '#BAE6FD' }}>
                                    <CardContent style={{ padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <View style={{ flex: 1, marginRight: 12 }}>
                                            <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 2 }}>
                                                {t('transcription.modal.icfProgressToggle')}
                                            </Text>
                                            <Text style={{ fontSize: 13, color: COLORS.text.muted }}>
                                                {t('transcription.modal.icfProgressDesc')}
                                            </Text>
                                        </View>
                                        <Switch
                                            value={includeInICFProgress}
                                            onValueChange={setIncludeInICFProgress}
                                            trackColor={{ false: '#E5E7EB', true: '#93c5fd' }}
                                            thumbColor={includeInICFProgress ? COLORS.primary : '#9CA3AF'}
                                        />
                                    </CardContent>
                                </Card>
                            </View>
                        </View>
                    </ScrollView>
                </SafeAreaView>
            </View>
        </Modal>
    );
}

export default TranscriptionModal;

const simpleStyles = StyleSheet.create({
    backdrop: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(21, 26, 35, 0.28)',
        padding: 24,
    },
    sheet: {
        width: 'min(640px, 96vw)' as any,
        maxHeight: '92vh' as any,
        overflow: 'auto' as any,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        padding: 22,
        boxShadow: reviewDesk.shadow,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        paddingBottom: 18,
        borderBottomWidth: 1,
        borderBottomColor: reviewDesk.line,
    },
    closeButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
    },
    headerTitle: {
        color: reviewDesk.ink,
        fontSize: 24,
        fontWeight: '800',
        letterSpacing: -0.2,
    },
    headerSubtitle: {
        color: reviewDesk.inkSoft,
        fontSize: 14,
        lineHeight: 20,
        marginTop: 4,
    },
    fileCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
        padding: 14,
        marginTop: 18,
    },
    fileIcon: {
        width: 48,
        height: 48,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: reviewDesk.accentSoft,
    },
    fileName: {
        color: reviewDesk.ink,
        fontSize: 15,
        fontWeight: '800',
    },
    fileMeta: {
        color: reviewDesk.muted,
        fontSize: 13,
        marginTop: 3,
    },
    readyPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        borderRadius: 999,
        backgroundColor: reviewDesk.successSoft,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    readyPillText: {
        color: reviewDesk.accent,
        fontSize: 12,
        fontWeight: '800',
    },
    defaultsCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        padding: 16,
        marginTop: 14,
        gap: 12,
    },
    pathCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.lineStrong,
        backgroundColor: reviewDesk.accentWash,
        padding: 16,
        marginTop: 14,
        gap: 12,
    },
    sectionTitle: {
        color: reviewDesk.ink,
        fontSize: 15,
        fontWeight: '800',
    },
    defaultRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    defaultText: {
        color: reviewDesk.inkSoft,
        fontSize: 14,
        fontWeight: '600',
    },
    pathRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    stepCircle: {
        width: 26,
        height: 26,
        borderRadius: 13,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: reviewDesk.accent,
    },
    stepCircleText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '800',
    },
    advancedBlock: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: '#FFFDF9',
        padding: 16,
        marginTop: 14,
        gap: 12,
    },
    segmentRow: {
        flexDirection: 'row',
        gap: 8,
    },
    segment: {
        flex: 1,
        minHeight: 38,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
    },
    segmentActive: {
        borderColor: reviewDesk.lineStrong,
        backgroundColor: reviewDesk.accentSoft,
    },
    segmentText: {
        color: reviewDesk.inkSoft,
        fontSize: 13,
        fontWeight: '700',
    },
    segmentTextActive: {
        color: reviewDesk.accent,
    },
    settingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        borderTopWidth: 1,
        borderTopColor: reviewDesk.line,
        paddingTop: 12,
    },
    settingTitle: {
        color: reviewDesk.ink,
        fontSize: 14,
        fontWeight: '800',
    },
    settingDesc: {
        color: reviewDesk.muted,
        fontSize: 12,
        lineHeight: 17,
        marginTop: 2,
    },
    textArea: {
        minHeight: 82,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: reviewDesk.line,
        backgroundColor: reviewDesk.card,
        color: reviewDesk.ink,
        padding: 12,
        fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
        textAlignVertical: 'top',
    },
    infoNote: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        borderRadius: 10,
        backgroundColor: reviewDesk.amberSoft,
        padding: 13,
        marginTop: 14,
    },
    infoText: {
        flex: 1,
        color: reviewDesk.inkSoft,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '600',
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 18,
    },
    advancedLink: {
        paddingHorizontal: 6,
        paddingVertical: 10,
    },
    advancedLinkText: {
        color: reviewDesk.accent,
        fontSize: 14,
        fontWeight: '800',
    },
    primaryAction: {
        minHeight: 48,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        borderRadius: 9,
        backgroundColor: reviewDesk.accent,
        paddingHorizontal: 22,
        boxShadow: '0 10px 22px rgba(30, 64, 175, 0.18)',
    } as any,
    primaryActionText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
});
